'use client';

import { Badge } from '@core/ui/primitives/badge';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@core/ui/primitives/card';
import { FaShieldAlt as ShieldCheck } from 'react-icons/fa';
import { useEffect } from 'react';

export function MetricsPanel({
  details,
  usagePercent,
  onReady,
}: {
  details?: { usage?: { usedGb?: number; quotaGb?: number; drafts?: number; scheduled?: number } };
  usagePercent: number;
  onReady?: (opts?: { immediate?: boolean }) => void;
}) {
  const usage = details?.usage ?? { usedGb: 0, quotaGb: 100, drafts: 0, scheduled: 0 };

  // This panel has no async needs; signal ready immediately
  useEffect(() => {
    onReady?.({ immediate: true });
  }, [onReady]);

  return (
    <div className='grid gap-4 md:grid-cols-2'>
      <Card>
        <CardHeader>
          <CardTitle className='text-sm'>Storage usage</CardTitle>
          <CardDescription>Each vault includes 100 GB by default.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className='space-y-2'>
            <div className='flex items-center justify-between text-xs text-muted-foreground'>
              <span>{usage.usedGb} GB used</span>
              <span>{usagePercent}%</span>
            </div>
            <div className='h-2 w-full rounded-full bg-muted'>
              <div className='h-2 rounded-full bg-primary transition-all' style={{ width: `${usagePercent}%` }} />
            </div>
            <p className='text-xs text-muted-foreground'>Quota: {usage.quotaGb} GB</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className='text-sm'>Lifecycle &amp; compliance</CardTitle>
          <CardDescription>Track publishing cadence and review load.</CardDescription>
        </CardHeader>
        <CardContent>
          <ul className='space-y-2 text-sm'>
            <li className='flex items-center justify-between rounded-lg border border-dashed px-3 py-2'>
              <span>Drafts awaiting review</span>
              <Badge variant='outline'>{usage.drafts}</Badge>
            </li>
            <li className='flex items-center justify-between rounded-lg border border-dashed px-3 py-2'>
              <span>Scheduled releases</span>
              <Badge variant='outline'>{usage.scheduled}</Badge>
            </li>
            <li className='flex items-center justify-between rounded-lg border border-dashed px-3 py-2'>
              <span>Compliance contacts</span>
              <div className='flex items-center gap-2 text-xs text-muted-foreground'>
                <ShieldCheck className='h-4 w-4 text-primary' />
                security@inovisum.com
              </div>
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}

export default MetricsPanel;
